<template>
    <div>
        <v-dialog ref="dialogRef" persistent width="fit-content" v-model="showMap" :key="componentKey">
            <v-card v-show="showMap">
                <v-card-title class="initial lighten-2" style="font-size: 1rem; font-weight: bold">
                    Map View
                    <v-btn style="height: 30px; width: 30px; box-shadow: none; position: absolute; right: 50px"
                        @click="toggleFullScreen">
                        <v-icon>{{ isFullScreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                    </v-btn>
                    <v-btn style="height: 30px; width: 30px; box-shadow: none; position: absolute; right: 10px"
                        @click="closeMap">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <l-map ref="map" v-if="showMap" @ready="onMapReady"
                    :style="{ zIndex: 0, width: dialogWidth + 'vw', height: dialogHeight + 'vh' }" :zoom="zoom"
                    :center="floorCenter" :bounds="floorBounds" :maxZoom="25">
                    <l-image-overlay :url="floorPlanUrl" :bounds="floorBounds" />

                    <!-- Markers for each POI -->
                    <l-marker v-for="poi in POIS" :key="poi.id" :icon="getMarkerIcon(poi)"
                        :lat-lng="[poi.locations[0].center.lat, poi.locations[0].center.long]">
                        <l-popup>
                            <v-card-title style="font-size: 0.9rem; font-weight: bold">
                                {{ poi.name }}
                            </v-card-title>
                            <v-card-subtitle>
                                <v-icon size="x-small" style="margin-right: 5px">mdi-tag</v-icon>
                                {{ poi.category?.name }}
                            </v-card-subtitle>
                            <v-card-subtitle>
                                <v-icon size="x-small" style="margin-right: 5px">mdi-clock-outline</v-icon>
                                {{ poi.time }}
                            </v-card-subtitle>
                            <v-card-subtitle>
                                <v-icon size="x-small" style="margin-right: 5px">mdi-account-group</v-icon>
                                {{ poi.capacity }}
                            </v-card-subtitle>
                            <v-card-subtitle style="font-size: 0.75rem">
                                <v-chip small :color="poi.status === 'booked' ? 'red' : 'green'" text-color="white"
                                    style="font-size: 0.7rem; height: 20px; border-radius: 0">
                                    <v-icon size="12" left>{{ poi.status === 'booked' ? 'mdi-lock' : 'mdi-lock-open'
                                        }}</v-icon>
                                    {{ poi.status }}
                                </v-chip>
                            </v-card-subtitle>
                        </l-popup>
                        <l-tooltip>
                            {{ poi.name }}
                        </l-tooltip>
                    </l-marker>

                    <!-- Polygons for each POI -->
                    <l-polygon v-for="poi in POIS" :key="'polygon-' + poi.id"
                        :lat-lngs="poi.locations[0].polygon.map(point => [point.lat, point.long])"
                        :color="poi.status === 'booked' ? '#FF9999' : '#99FF99'"
                        :fill-color="poi.status === 'booked' ? '#FFCCCC' : '#CCFFCC'" :fill-opacity="0.5" :weight="2">
                    </l-polygon>
                </l-map>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import 'leaflet/dist/leaflet.css';
import { LMap, LTooltip, LImageOverlay, LMarker, LPopup, LPolygon } from '@vue-leaflet/vue-leaflet';
import { ref, nextTick, defineExpose } from 'vue';
import { latLngBounds, latLng } from 'leaflet';
import { getMarkerIcon } from '@/oneVr/getMarkerIcon'


export default {
    name: 'SimpleMapView',
    components: {
        LMap,
        LImageOverlay,
        LTooltip,
        LMarker,
        LPopup,
        LPolygon,
    },
    setup() {
        const showMap = ref(false);
        const zoom = ref(20);
        const dialogWidth = ref(90);
        const dialogHeight = ref(90);
        const isFullScreen = ref(false);
        const map = ref(null);
        const componentKey = ref(0);
        const SELECTED_FLOOR = ref(null);
        const POIS = ref([]);

        const floorPlanUrl = '/assets/floorPlanBasic2D.png';

        // Real geo-bounds
        const floorBounds = latLngBounds(
            [53.54487831380667, 10.006931814357717], // SW
            [53.5459084479133, 10.00897590946902]    // NE
        );

        const floorCenter = latLng(
            (53.54487831380667 + 53.5459084479133) / 2,
            (10.006931814357717 + 10.00897590946902) / 2
        );

        const toggleFullScreen = () => {
            isFullScreen.value = !isFullScreen.value;
            dialogWidth.value = isFullScreen.value ? 100 : 90;
            dialogHeight.value = isFullScreen.value ? 100 : 90;
            adjustMapSize();
        };

        const openMap = async () => {
            let { pois } = await import('@/dummydata/pois');
            let { categories } = await import('@/dummydata/categories');
            let selectedFloorId = '365a50d0-a10a-4586-b79d-3a22577f85ca';
            pois = pois.filter(poi => poi.floors.ids[0] === selectedFloorId && poi?.name !== '2.32 PuMi');
            pois.forEach(poi => {
                poi.time = "06:00 - 22:00";
                poi.status = poi?.name?.includes('Toilet') ? 'free' : (Math.random() > 0.5 ? 'booked' : 'free');
                if (poi?.poiCategories.ids?.length > 0) {
                    poi.category = categories.find(cat => cat.id === poi?.poiCategories?.ids[0])
                    POIS.value.push(poi);
                }
                else {
                    poi.category = categories.find(cat => cat.name === 'Meeting room')
                    POIS.value.push(poi);
                }
            });

            showMap.value = true;
            adjustMapSize();
        };

        const closeMap = () => {
            showMap.value = false;
            zoom.value = 20;
            componentKey.value += 1;
        };

        const adjustMapSize = () => {
            nextTick(() => {
                setTimeout(() => {
                    if (map.value?.mapObject) {
                        map.value.mapObject.invalidateSize();
                        map.value.mapObject.fitBounds(floorBounds);
                    }
                }, 100);
            });
        };

        const onMapReady = () => {
            adjustMapSize();
            window.addEventListener('resize', handleResize);
            window.addEventListener('orientationchange', handleOrientationChange);
        };

        const handleResize = () => {
            adjustMapSize();
        };

        const handleOrientationChange = () => {
            zoom.value = 20;
            adjustMapSize();
            componentKey.value += 1;
        };

        return {
            showMap,
            zoom,
            dialogWidth,
            dialogHeight,
            isFullScreen,
            map,
            componentKey,
            floorPlanUrl,
            floorBounds,
            floorCenter,
            toggleFullScreen,
            openMap,
            closeMap,
            adjustMapSize,
            onMapReady,
            POIS,
            SELECTED_FLOOR,
            getMarkerIcon,
            handleResize,
            handleOrientationChange,

        };
    },
};
</script>

<style scoped lang="scss">
@import '~leaflet/dist/leaflet.css';

.v-dialog:not(.v-dialog--fullscreen) {
    max-height: 100%;
    width: 100%;
    margin: 0;
}

.v-dialog>.v-overlay__content {
    max-width: 100vw;
    max-height: 100vh;
    width: v-bind('dialogWidth + "vw"');
    height: v-bind('dialogHeight + "vh"');
}
</style>